<script setup>
import { ref, computed } from 'vue'
import { useOrganizationStore } from '@/stores/organizationStore'
import { useAuthStore } from '@/stores/authStore'

const orgStore = useOrganizationStore()
const authStore = useAuthStore()

// Billing Cycle State ('monthly' or 'yearly')
const billingCycle = ref('monthly')
const isSubmitting = ref(false)

// Pricing Logic for Pro Plan
const proPrice = computed(() => billingCycle.value === 'yearly' ? '$20' : '$24')
const proBillingText = computed(() => billingCycle.value === 'yearly' ? '$240 billed annually' : 'Billed monthly')

const handleSubscribe = async (tier) => {
  isSubmitting.value = true

  let plan = tier
  if (tier === 'pro') {
    plan = billingCycle.value === 'monthly' ? 'pro_monthly' : 'pro_yearly'
  }

  try {
    // Check if we have any existing organization to reuse
    if (authStore.organizations.length > 0) {
      // Use the first available organization
      const targetOrgId = authStore.organizations[0].id

      const response = await orgStore.createSubscriptionSession(targetOrgId, plan)
      if (response && response.sessionURL) {
        window.location.href = response.sessionURL
      }
    } else {
      // Create a new organization if none exist
      // The createOrganization action in the store handles the redirect to the session URL automatically
      await orgStore.createOrganization("My Organization", { plan })
    }

  } catch (error) {
    console.error("Failed to subscribe:", error)
    alert("An error occurred. Please try again.")
    isSubmitting.value = false
  }
}
</script>

<template>
  <div class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <!-- Background backdrop -->
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

      <div
        class="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
        <div class="flex flex-col items-center">
          <div class="flex items-center justify-center gap-2 pb-2 border-b border-gray-100">
            <svg class="w-8 h-8" viewBox="0 0 256 256">
              <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                <g>
                  <rect stroke="#000000" stroke-width="16" x="38" y="38" width="180" height="180" rx="18"></rect>
                  <rect fill="#000000" x="91" y="96" width="24" height="64" rx="12"></rect>
                  <rect fill="#000000" x="139" y="96" width="24" height="64" rx="12"></rect>
                  <rect fill="#000000" x="95" y="116" width="64" height="24" rx="12"></rect>
                  <rect fill="#000000" x="79" y="0" width="24" height="64" rx="12"></rect>
                  <rect fill="#000000" x="152" y="0" width="24" height="64" rx="12"></rect>
                  <rect fill="#000000" x="80" y="192" width="24" height="64" rx="12"></rect>
                  <rect fill="#000000" x="152" y="192" width="24" height="64" rx="12"></rect>
                  <rect fill="#000000" x="0" y="80" width="64" height="24" rx="12"></rect>
                  <rect fill="#000000" x="0" y="152" width="64" height="24" rx="12"></rect>
                  <rect fill="#000000" x="192" y="80" width="64" height="24" rx="12"></rect>
                  <rect fill="#000000" x="192" y="152" width="64" height="24" rx="12"></rect>
                </g>
              </g>
            </svg>
            <span class="text-xl font-extrabold tracking-tight text-gray-900">HasMCP Cloud</span>
          </div>
        </div>
        <div class="text-center my-8">
          <h2 class="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Select a Plan
          </h2>
          <p class="mt-4 text-lg text-gray-500">
            Please select a plan to activate your account and access features.
          </p>
        </div>

        <div class="flex justify-center mb-8">
          <div class="bg-gray-100 p-1 rounded-lg inline-flex">
            <button @click="billingCycle = 'monthly'" :class="[
              'px-6 py-1.5 text-sm font-medium rounded-md transition',
              billingCycle === 'monthly' ? 'bg-black text-white shadow-sm' : 'text-gray-500 hover:text-black'
            ]">
              Monthly
            </button>
            <button @click="billingCycle = 'yearly'" :class="[
              'px-6 py-1.5 text-sm font-medium rounded-md transition',
              billingCycle === 'yearly' ? 'bg-black text-white shadow-sm' : 'text-gray-500 hover:text-black'
            ]">
              Yearly
            </button>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          <!-- Hobby Plan -->
          <div
            class="p-6 rounded-xl border border-gray-200 bg-white hover:shadow-xl hover:border-gray-300 transition duration-300 flex flex-col">
            <h3 class="text-xl font-bold mb-2 text-gray-900">Cloud Hobby</h3>
            <p class="text-sm text-gray-500 mb-6">Start free and pay for what you use extra</p>
            <div class="mb-6">
              <span class="text-4xl font-extrabold text-gray-900">$0</span>
              <span class="text-gray-500 text-xs font-medium"> / month</span>
              <p class="text-xs text-gray-400 mt-1">billed at the end of the month</p>
            </div>

            <div class="mb-6">
              <button @click="handleSubscribe('hobby')" :disabled="isSubmitting"
                class="w-full bg-white text-black border border-gray-200 py-3 rounded-lg font-bold hover:bg-gray-50 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                Subscribe
              </button>
            </div>

            <div class="space-y-3 text-sm text-gray-600 flex-grow">
              <p class="flex items-center"><svg class="w-4 h-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                </svg> 50 MCP servers</p>
              <p class="flex items-center"><svg class="w-4 h-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                </svg> Free 250 tool calls included every month</p>
              <p class="flex items-center"><svg class="w-4 h-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                </svg> Additional 5k tool calls for $10</p>
            </div>
          </div>

          <!-- Pro Plan -->
          <div
            class="p-6 rounded-xl border border-gray-200 bg-white hover:shadow-xl hover:border-gray-300 transition duration-300 flex flex-col border-black/10 relative overflow-hidden">
            <div class="absolute top-0 right-0 bg-black text-white text-xs font-bold px-3 py-1 rounded-bl-lg">POPULAR
            </div>
            <h3 class="text-xl font-bold mb-2 text-gray-900">Cloud Pro</h3>
            <p class="text-sm text-gray-500 mb-6">For small teams and startups</p>
            <div class="mb-6">
              <span class="text-4xl font-extrabold text-gray-900">{{ proPrice }}</span>
              <span class="text-gray-500 text-xs font-medium"> per seat / month</span>
              <p class="text-xs text-gray-400 mt-1">{{ proBillingText }}</p>
            </div>

            <div class="mb-6">
              <button @click="handleSubscribe('pro')" :disabled="isSubmitting"
                class="w-full bg-black text-white py-3 rounded-lg font-bold hover:bg-gray-800 transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed">
                Subscribe
              </button>
            </div>

            <div class="space-y-3 text-sm text-gray-600 flex-grow">
              <p class="flex items-center"><svg class="w-4 h-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                </svg> 100 active MCP servers</p>
              <p class="flex items-center"><svg class="w-4 h-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                </svg> 100k tool calls per seat</p>
              <p class="flex items-center"><svg class="w-4 h-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                </svg> Custom domains</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
